import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Button } from '../Button';
import { Input } from '../Input';
import { Icon } from '../Icon';
import { SupportEmailCard } from '../SupportEmailCard';
import { PhoneSupportNotice } from '../PhoneSupportNotice';
import { useGuestSupportForm } from '../../shared/use-guest-support-form';
import styles from './GuestContactSupportForm.css';

const cx = classnames.bind(styles);

function GuestContactSupportForm({ onClose, email }) {
    const { submitForm, isPending, fields, changeField, errors, isSuccessful, isFail, resetForm } = useGuestSupportForm(email);

    const renderContent = () => {
        if (isFail) {
            return (
                <>
                    <div className={cx('noticeBlock', 'error')} role='alert'>
                        <Icon name='alertTriangle' />
                        <div>
                            <span>Message not sent</span>
                            <span>Please try again later, or contact us directly at the email address below.</span>
                        </div>
                    </div>
                    <SupportEmailCard />
                    <div className={styles.buttons}>
                        <Button
                            label='Try again'
                            className={styles.button}
                            onClick={resetForm}
                        />
                        <Button
                            label='Close'
                            className={styles.button}
                            type='secondary'
                            onClick={onClose}
                        />
                    </div>
                </>
            );
        }

        if (isSuccessful) {
            return (
                <>
                    <div className={cx('noticeBlock', 'success')} role='status'>
                        <Icon name='check' />
                        <div>
                            <span>Message sent successfully</span>
                            <span>
                                We'll reply within 15 minutes to <br /><strong>{fields.email}</strong>.
                            </span>
                        </div>
                    </div>
                    <Button
                        label='Close'
                        className={styles.button}
                        onClick={onClose}
                    />
                </>
            );
        }

        return (
            <>
                <p className={cx('description', 'withBottomIndent')}>Tell us what's wrong, and we'll take it from there.</p>
                <div className={styles.callBlock}>
                    <p className={styles.callBlockLabel}>Call us:</p>
                    <Button
                        label='+1 (424) 799-0744'
                        type='secondary'
                        link={{ to: 'tel:+14247990744' }}
                        className={styles.phone}
                    />
                </div>
                <PhoneSupportNotice withComingSoon />
                <p className={styles.description}>Or send a text message:</p>
                <Input
                    value={fields.email}
                    name='email'
                    onChange={changeField}
                    label={<>Your email (<span className={styles.required}>required</span>):</>}
                    errorText={errors.email}
                    inputProps={{ autoComplete: 'email', type: 'email' }}
                />
                <div>
                    <label htmlFor='message' className={styles.label}>
                        Your message (<span className={styles.required}>required</span>):
                    </label>
                    <textarea
                        id='message'
                        name='message'
                        className={cx('textarea', { error: Boolean(errors.message) })}
                        value={fields.message}
                        onChange={e => changeField('message', e.target.value)}
                        rows={5}
                    />
                    {Boolean(errors.message) && <p className={styles.alert}>{errors.message}</p>}
                </div>
                <Button
                    label='Send Message'
                    buttonProps={{ type: 'submit' }}
                    className={styles.button}
                    isLoading={isPending}
                />
            </>
        );
    };

    return (
        <div className={styles.root} role='dialog' aria-label='Contact Us 24/7'>
            <form
                className={styles.form}
                onSubmit={submitForm}
            >
                <div className={styles.header}>
                    <h1 className={styles.title}>Contact Us 24/7</h1>
                    <Button
                        type='secondary'
                        iconLeft={{ name: 'close' }}
                        onClick={onClose}
                        size='small'
                        buttonProps={{ 'aria-label': 'Close dialog' }}
                    />
                </div>
                {renderContent()}
            </form>
        </div>
    );
}

GuestContactSupportForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    email: PropTypes.string
};

GuestContactSupportForm.defaultProps = {
    email: ''
};

export {
    GuestContactSupportForm
};
